import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, UserX } from 'lucide-react';
import { api } from '@/lib/api';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminFormDialog } from '@/components/admin-form-dialog';

interface AdminRow {
  id: string;
  email: string;
  fullName: string;
  role: 'accountant' | 'superadmin';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export function AdminsListPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: () => api<{ items: AdminRow[] }>('/api/admin/admins'),
    enabled: !!me,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/admins/${id}/deactivate`, { method: 'PATCH' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }

  const isSuper = me.role === 'superadmin';

  return (
    <AdminShell>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admins</h1>
          <p className="text-sm text-muted-foreground">
            Equipo de contadores y superadmins de Actuo
          </p>
        </div>
        {isSuper && (
          <Button className="w-full sm:w-auto" onClick={() => setShowCreate(true)}>
            <Plus />
            Nuevo admin
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-background shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Último login</TableHead>
              {isSuper && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Cargando…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  No hay admins.
                </TableCell>
              </TableRow>
            )}
            {data?.items.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium text-foreground">{a.fullName}</TableCell>
                <TableCell className="text-muted-foreground">{a.email}</TableCell>
                <TableCell>
                  <Badge variant={a.role === 'superadmin' ? 'default' : 'secondary'}>
                    {a.role === 'superadmin' ? 'Superadmin' : 'Contador'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={a.isActive ? 'success' : 'destructive'}>
                    {a.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {a.lastLoginAt
                    ? new Date(a.lastLoginAt).toLocaleString('es-PY')
                    : '—'}
                </TableCell>
                {isSuper && (
                  <TableCell className="text-right">
                    {a.isActive && a.id !== me.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeactivateTarget(a)}
                        disabled={deactivateMutation.isPending}
                      >
                        <UserX />
                        Desactivar
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AdminFormDialog open={showCreate} onOpenChange={setShowCreate} />

      <AlertDialog
        open={!!deactivateTarget}
        onOpenChange={(open) => !open && setDeactivateTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar admin</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés desactivar a <strong>{deactivateTarget?.fullName}</strong>? No va a
              poder iniciar sesión hasta que lo reactives. Las asignaciones a tenants se mantienen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deactivateTarget) {
                  deactivateMutation.mutate(deactivateTarget.id);
                  setDeactivateTarget(null);
                }
              }}
              className="bg-destructive hover:opacity-90"
            >
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
